import React, { useEffect, useRef, useState } from 'react';
import { Layer, Rect } from 'react-konva';

import { ESC } from 'src/util/keycodes';
import { addOnDownListener, removeOnDownListener } from 'src/keyboardManager';
import idGeneratorAtor from 'src/util/idGeneratorAtor';
import type { KonvaEventObject } from 'konva/lib/Node';
import { useDragGesture } from 'src/hooks/useDragGesture';
import Ruler from './rulerContainer';

interface Point {
    x: number;
    y: number;
}

interface Props {
    container?: HTMLElement | null;
    canvasWidth?: number;
    canvasHeight?: number;
    scale: number;
    offset: Point;
    addModeEnabled?: boolean;
    toggleAddRulerMode: () => void;
    cursorChange: (cursor: string) => void;
    addRuler: (data: { id: string; point1: Point; point2: Point }) => void;
    rulerIds?: string[];
}

const Rulers: React.FC<Props> = ({
    canvasWidth,
    canvasHeight,
    cursorChange,
    container,
    addModeEnabled,
    scale,
    offset,
    toggleAddRulerMode,
    addRuler,
    rulerIds = [],
}) => {
    const idGenerator = useRef(idGeneratorAtor('ruler')).current;

    // `drawing`/`point1`/`point2` are kept as one state object, updated via a single `setState`
    // call per transition, rather than three independent `useState` hooks -- react-konva mounts
    // this whole tree under React's legacy root mode (not `createRoot`'s concurrent mode), so
    // updates triggered by Konva's own event dispatch (as `onMouseDown` below is) don't get React
    // 18's automatic batching the rest of the app enjoys. Three separate setters would each
    // trigger their own synchronous render; on the very first click, that produces an intermediate
    // render with `drawing: true` but `point1`/`point2` still unset, which crashes the preview
    // `Ruler` (it reads `point2.x` unconditionally). The original class component avoided this for
    // the same reason: `this.setState({ drawing: true, point1: ..., point2: ... })` was already a
    // single, atomic call.
    const [drawState, setDrawState] = useState<{ drawing: boolean; point1?: Point; point2?: Point }>({
        drawing: false,
    });
    const { drawing, point1, point2 } = drawState;

    // Snapshot-at-drawing-start bookkeeping that render() never reads directly and that can't be
    // derived from the current render (the click position the gesture started from) -- stays in a
    // plain ref rather than state so it doesn't trigger re-renders of its own. Everything else
    // (`point1`/`scale`) is read directly from props/state, since `useDragGesture` already
    // guarantees its `onDragMove` callback is always the latest one passed to it.
    const dragRef = useRef({ clientX: 0, clientY: 0 });

    const handleMouseMove = (e: MouseEvent) => {
        const { clientX, clientY } = dragRef.current;
        setDrawState((prev) => ({
            ...prev,
            point2: {
                x: prev.point1!.x - (clientX - e.clientX) / scale,
                y: prev.point1!.y - (clientY - e.clientY) / scale,
            },
        }));
    };

    // This is a click-move-click "draw" gesture, not a hold-drag-release one -- there's no
    // mouseup/touchend to end it automatically, so `endEvents: []` and a manual `stop()` call from
    // the "finish drawing" branch of `onMouseDown` below, instead of the default auto-stop.
    const { start: startTracking, stop: stopTracking } = useDragGesture({
        onDragMove: handleMouseMove,
        endEvents: [],
    });

    const onMouseDown = (e?: KonvaEventObject<MouseEvent>) => {
        if (e) {
            e.cancelBubble = true;
        }
        if (drawing) {
            addRuler({ id: idGenerator(), point1: point1!, point2: point2! });
            setDrawState({ drawing: false });
            stopTracking();
        } else {
            const canvasRect = container!.getBoundingClientRect();
            const startPoint = {
                x: (e!.evt.clientX - canvasRect.left - offset.x) / scale,
                y: (e!.evt.clientY - canvasRect.top - offset.y) / scale,
            };
            setDrawState({ drawing: true, point1: startPoint, point2: startPoint });
            dragRef.current.clientX = e!.evt.clientX;
            dragRef.current.clientY = e!.evt.clientY;
            startTracking();
        }
    };

    // Mirrors the keyboard-shortcut handler too, since it's registered once at mount via
    // `addOnDownListener`/`removeOnDownListener` (which match listeners by reference, same as
    // `addEventListener`) but needs to see the latest `addModeEnabled`/`drawing`/`onMouseDown` etc.
    // on every keypress, not just whatever was current when the effect first ran.
    const latestOnKeyDown = useRef<(e: KeyboardEvent) => void>(() => {});
    latestOnKeyDown.current = (e: KeyboardEvent) => {
        if (addModeEnabled && e.keyCode === ESC) {
            toggleAddRulerMode();
            cursorChange('default');
            if (drawing) {
                // Force the currently drawing ruler to be placed
                onMouseDown();
            }
        }
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => latestOnKeyDown.current(e);
        addOnDownListener(handleKeyDown);
        return () => removeOnDownListener(handleKeyDown);
    }, []);

    return (
        <Layer>
            {rulerIds.map((id) => (
                <Ruler
                    key={id}
                    id={id}
                    offset={offset}
                    scale={scale}
                    cursorChange={cursorChange}
                    container={container}
                />
            ))}
            {drawing && addModeEnabled && (
                // See the matching comment on the intermediate rename commit: this preview instance
                // supplies point1/point2/segments directly rather than via state lookup, which the
                // connected `Ruler`'s inferred prop types don't model but which already works
                // correctly at runtime (connect's default `mergeProps` only lets state override these
                // for an id that resolves to a real stored ruler, which `'drawing'` never does).
                <Ruler
                    {...({
                        id: 'drawing',
                        drawing: true,
                        point1: point1!,
                        point2: point2!,
                        segments: 10,
                        offset,
                        scale,
                        cursorChange,
                        container,
                    } as any)}
                />
            )}
            {addModeEnabled && (
                <Rect
                    width={canvasWidth}
                    height={canvasHeight}
                    onMouseDown={onMouseDown}
                    fill='rgba(0, 0, 0, 0.2)'
                    onMouseEnter={() => cursorChange('crosshair')}
                    onMouseLeave={() => cursorChange('default')}
                />
            )}
        </Layer>
    );
};

export default React.memo(Rulers);
